/**
 * @fileoverview Organization-admin sign-in (Spec 11). Email + password → a
 * session cookie; on success routes into the /org portal.
 *
 * Renders as the Organization tab INSIDE AuthPortalLayout, which owns the brand
 * mark, the portal tab strip, the heading and the "Back to home" link.
 *
 * Program viewers sign in here too: the same kind of account, and the portal
 * sends a viewer to the programs shared with them. The page says so, because
 * nothing else tells a school's coordinator to pick "Organization".
 */

import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LogIn } from "lucide-react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { safeOrgNext } from "@/lib/orgNext";
import { AuthCard, AuthField, AuthSubmit, AuthNote, authLinkCls } from "@/components/auth/authUi";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const OrgLoginPage = () => {
  const navigate = useNavigate();
  // Where a signed-out link was going (an email's "Open the program"); only a
  // page inside the org portal is honoured (lib/orgNext).
  const [params] = useSearchParams();
  const next = safeOrgNext(params.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim()) || !password) {
      setError("Enter your email and password");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await orgApi.post("/org/auth/login", { email: email.trim(), password });
      navigate(next || "/org");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Invalid email or password"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        <AuthCard>
          <AuthField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
            required
            placeholder="you@your-org.com"
            autoComplete="email"
            testId="org-login-email"
          />
          <AuthField
            label="Password"
            type="password"
            reveal
            value={password}
            onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
            required
            placeholder="Your password"
            autoComplete="current-password"
            testId="org-login-password"
          >
            <div className="mt-2 text-right">
              <Link to="/org/forgot-password" className={`text-xs ${authLinkCls}`}>
                Forgot password?
              </Link>
            </div>
          </AuthField>
          {error && (
            <p className="text-xs text-[var(--state-danger)]" data-testid="org-login-error">{error}</p>
          )}
        </AuthCard>

        <AuthSubmit submitting={submitting} icon={LogIn} testId="org-login-submit">
          Sign in
        </AuthSubmit>
        <AuthNote className="text-center" data-testid="org-login-who">
          Administrators and program viewers both sign in here.
        </AuthNote>
      </form>

      <div className="mt-6 text-center">
        <AuthNote>
          Setting up a new organization?{" "}
          <Link to="/org/signup" className={authLinkCls}>
            Create an organization
          </Link>
        </AuthNote>
      </div>
    </div>
  );
};

export default OrgLoginPage;
